import type { BrowserViewportResizeDirection } from "~/browser/browserViewportLayout";
import type { PreviewMiniPlayerSize } from "~/previewMiniPlayerStore";

import type { PreviewMiniPlayerObstacles } from "./previewMiniPlayerLayout";

/**
 * Chrome shared by the floating windows over the chat column (the browser
 * mini player and floating figures): measuring the column and the composer
 * they must stay clear of, and the invisible edge handles they resize from.
 *
 * @module components/preview/miniPlayerChrome
 */

export interface MiniPlayerLayout {
  readonly container: PreviewMiniPlayerSize;
  readonly obstacles: PreviewMiniPlayerObstacles;
}

export const sameMiniPlayerLayout = (a: MiniPlayerLayout, b: MiniPlayerLayout) =>
  a.container.width === b.container.width &&
  a.container.height === b.container.height &&
  (a.obstacles.composer === b.obstacles.composer ||
    (a.obstacles.composer !== null &&
      b.obstacles.composer !== null &&
      a.obstacles.composer.left === b.obstacles.composer.left &&
      a.obstacles.composer.right === b.obstacles.composer.right &&
      a.obstacles.composer.height === b.obstacles.composer.height));

/**
 * Measures the chat column and the composer in the column's coordinates. The
 * composer's columns come from its centered stack, not the full-width overlay,
 * so the margins beside it stay open to the player.
 */
export function measureMiniPlayerLayout(
  container: HTMLElement,
  composerOverlay: HTMLElement | null,
): MiniPlayerLayout {
  const containerRect = container.getBoundingClientRect();
  const stackRect = composerOverlay
    ?.querySelector('[data-chat-composer-form="true"]')
    ?.getBoundingClientRect();
  const overlayRect = composerOverlay?.getBoundingClientRect();
  return {
    container: { width: container.clientWidth, height: container.clientHeight },
    obstacles: {
      composer:
        overlayRect && overlayRect.height > 0
          ? {
              left: Math.floor(
                (stackRect && stackRect.width > 0 ? stackRect.left : overlayRect.left) -
                  containerRect.left,
              ),
              right: Math.ceil(
                (stackRect && stackRect.width > 0 ? stackRect.right : overlayRect.right) -
                  containerRect.left,
              ),
              height: Math.ceil(overlayRect.height),
            }
          : null,
    },
  };
}

// Grab zones straddling each edge and corner. The two bottom corners also
// carry a visible grip (see ThreadPreviewMiniPlayer) so resizing is findable.
export const MINI_PLAYER_RESIZE_HANDLES: ReadonlyArray<{
  readonly direction: BrowserViewportResizeDirection;
  readonly className: string;
}> = [
  { direction: "north", className: "inset-x-0 -top-1.5 h-3 cursor-ns-resize" },
  { direction: "south", className: "inset-x-0 -bottom-1.5 h-3 cursor-ns-resize" },
  { direction: "west", className: "inset-y-0 -left-1.5 w-3 cursor-ew-resize" },
  { direction: "east", className: "inset-y-0 -right-1.5 w-3 cursor-ew-resize" },
  { direction: "northwest", className: "-left-2 -top-2 size-5 cursor-nwse-resize" },
  { direction: "northeast", className: "-right-2 -top-2 size-5 cursor-nesw-resize" },
  {
    direction: "southwest",
    className: "-bottom-1 -left-1 flex size-7 items-center justify-center cursor-nesw-resize",
  },
  {
    direction: "southeast",
    className: "-bottom-1 -right-1 flex size-7 items-center justify-center cursor-nwse-resize",
  },
];
